/**
 * KpiTiles — the admin control room's 4-up KPI strip (mockup `#s-actrl`
 * `.kpis` / `.kpi`): Pending approval / Active annotators / Projects /
 * Blocked · pool empty. The "Blocked" tile switches to warn styling when
 * its count is > 0 (mockup `.kpi.alert`) — everything else stays neutral.
 *
 * Pure presentational — `AdminControlRoom` derives every count from the
 * fanned-out enrollments/pool queries and passes them down as plain props.
 */

import React from "react";

interface TileProps {
  label: string;
  value: number;
  detail: string;
  // Task 15: palette restricted to blue/red/green only — the old non-blue/
  // red/green tone is gone; "sig" (blue) takes its place for the Pending
  // approval tile.
  tone?: "neutral" | "sig" | "warn";
}

const EDGE_TONE_CLASSES: Record<NonNullable<TileProps["tone"]>, string> = {
  neutral: "bg-border-strong",
  sig: "bg-signal",
  warn: "bg-warn",
};

const Tile: React.FC<TileProps> = ({ label, value, detail, tone = "neutral" }) => (
  <div className="relative rounded border border-border/50 bg-surface p-4 pl-5">
    <span
      aria-hidden="true"
      className={`absolute bottom-3 left-0 top-3 w-0.5 ${EDGE_TONE_CLASSES[tone]}`}
    />
    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">{label}</div>
    <div
      className={`mt-1.5 text-[26px] font-semibold ${tone === "warn" ? "text-warn" : "text-fg"}`}
    >
      {value}
    </div>
    <div className="mt-1 text-[11.5px] text-fg-muted">{detail}</div>
  </div>
);

export interface KpiTilesProps {
  pendingApprovalCount: number;
  activeAnnotatorCount: number;
  projectCount: number;
  acceptingCount: number;
  blockedCount: number;
  blockedDetail: string;
}

export const KpiTiles: React.FC<KpiTilesProps> = ({
  pendingApprovalCount,
  activeAnnotatorCount,
  projectCount,
  acceptingCount,
  blockedCount,
  blockedDetail,
}) => (
  <div className="mb-6 grid grid-cols-2 gap-3.5 md:grid-cols-4">
    <Tile
      label="Pending approval"
      value={pendingApprovalCount}
      detail="in this category"
      tone="sig"
    />
    <Tile
      label="Active annotators"
      value={activeAnnotatorCount}
      detail="labeling now"
      tone="neutral"
    />
    <Tile
      label="Projects"
      value={projectCount}
      detail={`${acceptingCount} accepting new`}
      tone="neutral"
    />
    <Tile
      label="Blocked · pool empty"
      value={blockedCount}
      detail={blockedDetail}
      tone={blockedCount > 0 ? "warn" : "neutral"}
    />
  </div>
);

export default KpiTiles;
